import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import AppPageIntro from '@/components/AppPageIntro';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { PiggyBank, Plus, Save, Trash2, Pencil, X, Target } from 'lucide-react';
import { toast } from 'sonner';

const reservationCategoryOptions = [
  { value: 'vacation', label: 'Vakantie' },
  { value: 'car', label: 'Auto-onderhoud' },
  { value: 'insurance', label: 'Verzekering' },
  { value: 'buffer', label: 'Buffer' },
  { value: 'tax', label: 'Belasting' },
  { value: 'other', label: 'Overig' },
];

const reservationFrequencyLabels = {
  monthly: 'per maand',
  quarterly: 'per kwartaal',
  yearly: 'per jaar',
};

const emptyGoalForm = {
  name: '',
  target_amount: '',
  current_amount: '',
  goal_pot_id: '',
  planning_mode: 'date',
  target_date: '',
  target_months: '',
  monthly_contribution: '',
};

const emptyPotForm = {
  name: '',
  type: 'virtual_pot',
  account_id: '',
  account_name: '',
  bank_iban: '',
  current_balance: '',
  balance_source: 'manual',
};

const emptyReservationForm = {
  name: '',
  category: 'vacation',
  target_amount: '',
  current_amount: '',
  contribution_amount: '',
  contribution_frequency: 'monthly',
};

const goalStatusConfig = {
  on_track: {
    label: 'Op schema',
    badgeClass: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    noteClass: 'text-emerald-700',
  },
  behind: {
    label: 'Achter',
    badgeClass: 'bg-amber-50 text-amber-700 border border-amber-200',
    noteClass: 'text-amber-700',
  },
  completed: {
    label: 'Behaald',
    badgeClass: 'bg-primary/10 text-primary border border-primary/20',
    noteClass: 'text-primary',
  },
};

export default function Reservations() {
  const [loading, setLoading] = useState(true);
  const [savingsGoals, setSavingsGoals] = useState([]);
  const [goalPots, setGoalPots] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [showPotForm, setShowPotForm] = useState(false);
  const [showReservationForm, setShowReservationForm] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editingPotId, setEditingPotId] = useState(null);
  const [editingReservationId, setEditingReservationId] = useState(null);
  const [goalForm, setGoalForm] = useState(emptyGoalForm);
  const [potForm, setPotForm] = useState(emptyPotForm);
  const [reservationForm, setReservationForm] = useState(emptyReservationForm);

  const fetchData = async () => {
    try {
      const [goalsRes, potsRes, accountsRes, reservationsRes] = await Promise.all([
        apiClient.get('/savings/goals').catch(() => ({ data: [] })),
        apiClient.get('/goal-pots').catch(() => ({ data: [] })),
        apiClient.get('/accounts').catch(() => ({ data: [] })),
        apiClient.get('/reservations').catch(() => ({ data: [] })),
      ]);
      setSavingsGoals(Array.isArray(goalsRes.data) ? goalsRes.data : []);
      setGoalPots(Array.isArray(potsRes.data) ? potsRes.data : []);
      setAccounts(Array.isArray(accountsRes.data) ? accountsRes.data : []);
      setReservations(Array.isArray(reservationsRes.data) ? reservationsRes.data : []);
    } catch (error) {
      console.error('Error fetching reservations page:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const reservationsSummary = useMemo(() => {
    const reservedTotal = reservations.reduce((sum, item) => sum + Number(item.current_amount || 0), 0);
    const targetTotal = reservations.reduce((sum, item) => sum + Number(item.target_amount || 0), 0);
    const monthlyImpact = reservations.reduce((sum, item) => sum + Number(item.monthly_equivalent || 0), 0);
    return {
      reservedTotal,
      targetTotal,
      monthlyImpact,
      count: reservations.length,
    };
  }, [reservations]);

  const goalsSummary = useMemo(() => {
    const currentTotal = savingsGoals.reduce((sum, item) => sum + Number(item.current_amount || 0), 0);
    const targetTotal = savingsGoals.reduce((sum, item) => sum + Number(item.target_amount || 0), 0);
    const monthlyContribution = savingsGoals.reduce((sum, item) => sum + Number(item.monthly_contribution || 0), 0);
    const requiredMonthly = savingsGoals.reduce((sum, item) => sum + Number(item.required_monthly_amount || 0), 0);
    return {
      currentTotal,
      targetTotal,
      monthlyContribution,
      requiredMonthly,
      count: savingsGoals.length,
    };
  }, [savingsGoals]);

  const potsSummary = useMemo(() => {
    const totalBalance = goalPots.reduce((sum, item) => sum + Number(item.current_balance || 0), 0);
    const linkedGoals = goalPots.reduce((sum, item) => sum + Number(item.goal_count || 0), 0);
    return {
      totalBalance,
      linkedGoals,
      count: goalPots.length,
    };
  }, [goalPots]);

  const goalPreview = useMemo(() => {
    const targetAmount = Number(goalForm.target_amount || 0);
    const currentAmount = Number(goalForm.current_amount || 0);
    const remainingAmount = Math.max(targetAmount - currentAmount, 0);
    const manualContribution = Number(goalForm.monthly_contribution || 0);

    let monthsRemaining = 0;
    if (goalForm.planning_mode === 'months') {
      monthsRemaining = Math.max(Number(goalForm.target_months || 0), 0);
    } else if (goalForm.target_date) {
      const today = new Date();
      const targetDate = new Date(goalForm.target_date);
      if (!Number.isNaN(targetDate.getTime())) {
        const monthDiff =
          (targetDate.getFullYear() - today.getFullYear()) * 12 +
          (targetDate.getMonth() - today.getMonth()) +
          (targetDate.getDate() >= today.getDate() ? 1 : 0);
        monthsRemaining = Math.max(monthDiff, 1);
      }
    }

    const requiredMonthlyAmount =
      remainingAmount > 0 && monthsRemaining > 0 ? remainingAmount / monthsRemaining : 0;
    const monthlyContribution = manualContribution > 0 ? manualContribution : requiredMonthlyAmount;
    let status = 'on_track';
    if (remainingAmount <= 0) {
      status = 'completed';
    } else if (requiredMonthlyAmount > 0 && monthlyContribution + 0.005 < requiredMonthlyAmount) {
      status = 'behind';
    }
    return {
      remainingAmount,
      monthsRemaining,
      requiredMonthlyAmount,
      monthlyContribution,
      status,
      behindAmount: Math.max(requiredMonthlyAmount - monthlyContribution, 0),
      progress: targetAmount > 0 ? Math.min((currentAmount / targetAmount) * 100, 100) : 0,
    };
  }, [goalForm]);

  const resetGoalForm = () => {
    setEditingGoalId(null);
    setShowGoalForm(false);
    setGoalForm(emptyGoalForm);
  };

  const resetPotForm = () => {
    setEditingPotId(null);
    setShowPotForm(false);
    setPotForm(emptyPotForm);
  };

  const resetReservationForm = () => {
    setEditingReservationId(null);
    setShowReservationForm(false);
    setReservationForm(emptyReservationForm);
  };

  const saveGoal = async (e) => {
    e.preventDefault();
    const computedMonthly =
      Number(goalForm.monthly_contribution || 0) > 0
        ? Number(goalForm.monthly_contribution)
        : Number(goalPreview.requiredMonthlyAmount || 0);
    const payload = {
      name: goalForm.name,
      target_amount: Number(goalForm.target_amount),
      current_amount: Number(goalForm.current_amount),
      goal_pot_id: goalForm.goal_pot_id || null,
      monthly_contribution: computedMonthly,
      target_date: goalForm.planning_mode === 'date' ? goalForm.target_date : null,
      target_months: goalForm.planning_mode === 'months' ? Number(goalForm.target_months || 0) : null,
    };
    try {
      if (editingGoalId) {
        await apiClient.put(`/savings/goals/${editingGoalId}`, payload);
        toast.success('Spaardoel bijgewerkt');
      } else {
        await apiClient.post('/savings/goals', payload);
        toast.success('Spaardoel toegevoegd');
      }
      resetGoalForm();
      fetchData();
    } catch (error) {
      console.error('Error saving savings goal:', error);
      toast.error('We konden je spaardoel niet opslaan.');
    }
  };

  const startEditGoal = (goal) => {
    setShowGoalForm(true);
    setEditingGoalId(goal.id);
    setGoalForm({
      name: goal.name,
      target_amount: String(goal.target_amount),
      current_amount: String(goal.current_amount),
      goal_pot_id: goal.goal_pot_id || '',
      planning_mode: goal.target_months ? 'months' : 'date',
      target_date: goal.target_date || '',
      target_months: goal.target_months ? String(goal.target_months) : '',
      monthly_contribution: String(goal.monthly_contribution),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const savePot = async (e) => {
    e.preventDefault();
    const selectedAccount = accounts.find((account) => account.id === potForm.account_id);
    const payload = {
      name: potForm.name,
      type: potForm.type,
      account_id: potForm.account_id || null,
      account_name: selectedAccount?.name || potForm.account_name || null,
      bank_iban: selectedAccount?.iban || potForm.bank_iban || null,
      current_balance: Number(potForm.current_balance || 0),
      balance_source: potForm.balance_source,
    };
    try {
      if (editingPotId) {
        await apiClient.put(`/goal-pots/${editingPotId}`, payload);
        toast.success('Potje bijgewerkt');
      } else {
        await apiClient.post('/goal-pots', payload);
        toast.success('Potje toegevoegd');
      }
      resetPotForm();
      fetchData();
    } catch (error) {
      console.error('Error saving goal pot:', error);
      toast.error(error?.response?.data?.detail || 'We konden je potje niet opslaan.');
    }
  };

  const startEditPot = (pot) => {
    setShowPotForm(true);
    setEditingPotId(pot.id);
    setPotForm({
      name: pot.name,
      type: pot.type || 'virtual_pot',
      account_id: pot.account_id || '',
      account_name: pot.account_name || '',
      bank_iban: pot.bank_iban || '',
      current_balance: String(pot.current_balance || 0),
      balance_source: pot.balance_source || 'manual',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deletePot = async (potId) => {
    if (!window.confirm('Potje verwijderen?')) return;
    try {
      await apiClient.delete(`/goal-pots/${potId}`);
      toast.success('Potje verwijderd');
      fetchData();
    } catch (error) {
      console.error('Error deleting goal pot:', error);
      toast.error(error?.response?.data?.detail || 'We konden je potje niet verwijderen.');
    }
  };

  const deleteGoal = async (goalId) => {
    if (!window.confirm('Spaardoel verwijderen?')) return;
    try {
      await apiClient.delete(`/savings/goals/${goalId}`);
      toast.success('Spaardoel verwijderd');
      fetchData();
    } catch (error) {
      console.error('Error deleting savings goal:', error);
      toast.error('We konden je spaardoel niet verwijderen.');
    }
  };

  const saveReservation = async (e) => {
    e.preventDefault();
    const payload = {
      ...reservationForm,
      target_amount: Number(reservationForm.target_amount),
      current_amount: Number(reservationForm.current_amount),
      contribution_amount: Number(reservationForm.contribution_amount),
    };
    try {
      if (editingReservationId) {
        await apiClient.put(`/reservations/${editingReservationId}`, payload);
        toast.success('Reservering bijgewerkt');
      } else {
        await apiClient.post('/reservations', payload);
        toast.success('Reservering toegevoegd');
      }
      resetReservationForm();
      fetchData();
    } catch (error) {
      console.error('Error saving reservation:', error);
      toast.error('We konden je reservering niet opslaan.');
    }
  };

  const startEditReservation = (reservation) => {
    setShowReservationForm(true);
    setEditingReservationId(reservation.id);
    setReservationForm({
      name: reservation.name,
      category: reservation.category || 'other',
      target_amount: String(reservation.target_amount || 0),
      current_amount: String(reservation.current_amount || 0),
      contribution_amount: String(reservation.contribution_amount || 0),
      contribution_frequency: reservation.contribution_frequency || 'monthly',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteReservation = async (reservationId) => {
    if (!window.confirm('Reservering verwijderen?')) return;
    try {
      await apiClient.delete(`/reservations/${reservationId}`);
      toast.success('Reservering verwijderd');
      fetchData();
    } catch (error) {
      console.error('Error deleting reservation:', error);
      toast.error('We konden je reservering niet verwijderen.');
    }
  };

  if (loading) {
    return <div className="h-24 animate-pulse rounded-xl bg-muted" />;
  }

  return (
    <div className="space-y-6">
      <AppPageIntro
        eyebrow="Potjes en doelen"
        title="Reserveringen"
        description="Zet geld apart voor wat eraan komt, zodat je dashboard rustig blijft en je maandruimte niet steeds opnieuw hoeft te dragen."
        badges={['Spaardoelen', 'Potjes', 'Maandimpact']}
      />

      <div className="grid gap-4 md:grid-cols-4">
        <Card className="shadow-card">
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground">Totaal gereserveerd</p>
            <p className="mt-1 font-mono text-2xl font-semibold">€{reservationsSummary.reservedTotal.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground">Doelbedrag reserveringen</p>
            <p className="mt-1 font-mono text-2xl font-semibold">€{reservationsSummary.targetTotal.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground">Maandimpact</p>
            <p className="mt-1 font-mono text-2xl font-semibold">€{reservationsSummary.monthlyImpact.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground">Actieve potjes</p>
            <p className="mt-1 font-mono text-2xl font-semibold">{reservationsSummary.count + potsSummary.count}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-card border-primary/15">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="font-heading flex items-center gap-2">
              <PiggyBank className="h-5 w-5 text-primary" />
              Potjes
            </CardTitle>
            <Button
              variant={showPotForm ? 'outline' : 'default'}
              size="sm"
              className="rounded-full"
              onClick={() => setShowPotForm((value) => !value)}
            >
              {showPotForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {showPotForm ? 'Sluit' : 'Nieuw potje'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 rounded-xl border border-primary/10 bg-primary/[0.03] p-4 md:grid-cols-3">
            <div>
              <p className="text-xs text-muted-foreground">Totaal potjessaldo</p>
              <p className="mt-1 font-mono text-lg font-semibold">€{potsSummary.totalBalance.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Potjes</p>
              <p className="mt-1 font-mono text-lg font-semibold">{potsSummary.count}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Gekoppelde doelen</p>
              <p className="mt-1 font-mono text-lg font-semibold">{potsSummary.linkedGoals}</p>
            </div>
          </div>

          {showPotForm && (
            <form onSubmit={savePot} className="grid grid-cols-1 gap-3 rounded-xl border bg-background p-4 md:grid-cols-2">
              <Input
                placeholder="Naam potje"
                value={potForm.name}
                onChange={(e) => setPotForm((p) => ({ ...p, name: e.target.value }))}
                required
              />
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={potForm.type}
                onChange={(e) => setPotForm((p) => ({ ...p, type: e.target.value }))}
              >
                <option value="virtual_pot">Virtueel potje</option>
                <option value="bank_pot">Bankpotje</option>
              </select>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={potForm.account_id}
                onChange={(e) => setPotForm((p) => ({ ...p, account_id: e.target.value }))}
              >
                <option value="">Geen gekoppelde rekening</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {(account.bank ? `${account.bank} - ` : '') + account.name}
                  </option>
                ))}
              </select>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={potForm.balance_source}
                onChange={(e) => setPotForm((p) => ({ ...p, balance_source: e.target.value }))}
              >
                <option value="manual">Saldo handmatig</option>
                <option value="linked_account">Saldo gekoppelde rekening</option>
              </select>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Huidig saldo"
                value={potForm.current_balance}
                onChange={(e) => setPotForm((p) => ({ ...p, current_balance: e.target.value }))}
                required
              />
              <Input
                placeholder="IBAN (optioneel)"
                value={potForm.bank_iban}
                onChange={(e) => setPotForm((p) => ({ ...p, bank_iban: e.target.value }))}
              />
              <div className="md:col-span-2 flex flex-wrap gap-2">
                <Button type="submit">
                  {editingPotId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  {editingPotId ? 'Opslaan' : 'Toevoegen'}
                </Button>
                <Button type="button" variant="outline" onClick={resetPotForm}>
                  <X className="w-4 h-4 mr-2" />
                  Annuleren
                </Button>
              </div>
            </form>
          )}

          {goalPots.length === 0 ? (
            <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
              Nog geen potjes. Maak een bankpotje of virtueel potje aan en koppel het daarna aan een spaardoel.
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {goalPots.map((pot) => (
                <div key={pot.id} className="space-y-3 rounded-xl border bg-background p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium">{pot.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {pot.type === 'bank_pot' ? 'Bankpotje' : 'Virtueel potje'}
                        {pot.account_name ? ` · ${pot.account_name}` : ''}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="secondary" onClick={() => startEditPot(pot)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => deletePot(pot.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Saldo</span>
                    <span className="font-mono font-semibold">€{Number(pot.current_balance || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{pot.balance_source === 'linked_account' ? 'Bron: gekoppelde rekening' : 'Bron: handmatig'}</span>
                    <span>{Number(pot.goal_count || 0)} doel(en)</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="shadow-card border-primary/15">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="font-heading flex items-center gap-2">
                <PiggyBank className="h-5 w-5 text-primary" />
                Reserveringen
              </CardTitle>
              <Button
                variant={showReservationForm ? 'outline' : 'default'}
                size="sm"
                className="rounded-full"
                onClick={() => setShowReservationForm((value) => !value)}
              >
                {showReservationForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {showReservationForm ? 'Sluit' : 'Nieuwe reservering'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {showReservationForm && (
              <form onSubmit={saveReservation} className="grid grid-cols-1 gap-3 rounded-xl border bg-background p-4 md:grid-cols-2">
                <Input
                  placeholder="Naam reservering"
                  value={reservationForm.name}
                  onChange={(e) => setReservationForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={reservationForm.category}
                  onChange={(e) => setReservationForm((p) => ({ ...p, category: e.target.value }))}
                >
                  {reservationCategoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Doelbedrag"
                  value={reservationForm.target_amount}
                  onChange={(e) => setReservationForm((p) => ({ ...p, target_amount: e.target.value }))}
                  required
                />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Nu gereserveerd"
                  value={reservationForm.current_amount}
                  onChange={(e) => setReservationForm((p) => ({ ...p, current_amount: e.target.value }))}
                  required
                />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Inleg per periode"
                  value={reservationForm.contribution_amount}
                  onChange={(e) => setReservationForm((p) => ({ ...p, contribution_amount: e.target.value }))}
                  required
                />
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={reservationForm.contribution_frequency}
                  onChange={(e) => setReservationForm((p) => ({ ...p, contribution_frequency: e.target.value }))}
                >
                  <option value="monthly">Per maand</option>
                  <option value="quarterly">Per kwartaal</option>
                  <option value="yearly">Per jaar</option>
                </select>
                <div className="md:col-span-2 flex flex-wrap gap-2">
                  <Button type="submit">
                    {editingReservationId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    {editingReservationId ? 'Opslaan' : 'Toevoegen'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetReservationForm}>
                    <X className="w-4 h-4 mr-2" />
                    Annuleren
                  </Button>
                </div>
              </form>
            )}

            {reservations.length === 0 ? (
              <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
                Nog geen reserveringen. Begin met een potje voor vakantie, buffer of een jaarlijkse rekening.
              </div>
            ) : (
              <div className="space-y-3">
                {reservations.map((reservation) => (
                  <div key={reservation.id} className="space-y-3 rounded-xl border bg-background p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{reservation.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {reservationCategoryOptions.find((option) => option.value === reservation.category)?.label || 'Overig'}
                          {' · '}
                          {reservationFrequencyLabels[reservation.contribution_frequency] || 'per maand'}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="secondary" onClick={() => startEditReservation(reservation)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteReservation(reservation.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Gereserveerd</span>
                      <span className="font-mono font-semibold">
                        €{Number(reservation.current_amount || 0).toFixed(2)} / €{Number(reservation.target_amount || 0).toFixed(2)}
                      </span>
                    </div>
                    <Progress value={Math.min(Number(reservation.progress || 0), 100)} className="h-2" />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Nog €{Number(reservation.remaining || 0).toFixed(2)} nodig</span>
                      <span>Maandimpact €{Number(reservation.monthly_equivalent || 0).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="font-heading flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Spaardoelen
              </CardTitle>
              <Button
                variant={showGoalForm ? 'outline' : 'default'}
                size="sm"
                className="rounded-full"
                onClick={() => setShowGoalForm((value) => !value)}
              >
                {showGoalForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {showGoalForm ? 'Sluit' : 'Nieuw spaardoel'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 rounded-xl border border-primary/10 bg-primary/[0.03] p-4 md:grid-cols-3">
              <div>
                <p className="text-xs text-muted-foreground">Nu apart gezet</p>
                <p className="mt-1 font-mono text-lg font-semibold">€{goalsSummary.currentTotal.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Benodigd per maand</p>
                <p className="mt-1 font-mono text-lg font-semibold">€{goalsSummary.requiredMonthly.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Nu ingesteld per maand</p>
                <p className="mt-1 font-mono text-lg font-semibold">€{goalsSummary.monthlyContribution.toFixed(2)}</p>
              </div>
            </div>

            {showGoalForm && (
              <form onSubmit={saveGoal} className="grid grid-cols-1 gap-3 rounded-xl border bg-background p-4 md:grid-cols-2">
                <Input
                  placeholder="Naam doel"
                  value={goalForm.name}
                  onChange={(e) => setGoalForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={goalForm.goal_pot_id}
                  onChange={(e) => setGoalForm((p) => ({ ...p, goal_pot_id: e.target.value }))}
                >
                  <option value="">Geen gekoppeld potje</option>
                  {goalPots.map((pot) => (
                    <option key={pot.id} value={pot.id}>
                      {pot.name} ({pot.type === 'bank_pot' ? 'Bankpotje' : 'Virtueel potje'})
                    </option>
                  ))}
                </select>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={goalForm.planning_mode}
                  onChange={(e) =>
                    setGoalForm((p) => ({
                      ...p,
                      planning_mode: e.target.value,
                      target_date: e.target.value === 'date' ? p.target_date : '',
                      target_months: e.target.value === 'months' ? p.target_months : '',
                    }))
                  }
                >
                  <option value="date">Werk naar einddatum</option>
                  <option value="months">Werk met aantal maanden</option>
                </select>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Doelbedrag"
                  value={goalForm.target_amount}
                  onChange={(e) => setGoalForm((p) => ({ ...p, target_amount: e.target.value }))}
                  required
                />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Huidig bedrag"
                  value={goalForm.current_amount}
                  onChange={(e) => setGoalForm((p) => ({ ...p, current_amount: e.target.value }))}
                  required
                />
                {goalForm.planning_mode === 'date' ? (
                  <Input
                    type="date"
                    value={goalForm.target_date}
                    onChange={(e) => setGoalForm((p) => ({ ...p, target_date: e.target.value }))}
                    required
                  />
                ) : (
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Aantal maanden tot doel"
                    value={goalForm.target_months}
                    onChange={(e) => setGoalForm((p) => ({ ...p, target_months: e.target.value }))}
                    required
                  />
                )}
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Maandelijkse inleg (optioneel)"
                  value={goalForm.monthly_contribution}
                  onChange={(e) => setGoalForm((p) => ({ ...p, monthly_contribution: e.target.value }))}
                />
                <div className="md:col-span-2 rounded-xl border border-primary/10 bg-primary/[0.03] p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">Slim rekenvoorstel</p>
                      <p className="text-xs text-muted-foreground">
                        We rekenen uit wat je per maand nodig hebt om op tijd uit te komen.
                      </p>
                    </div>
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                        goalStatusConfig[goalPreview.status]?.badgeClass || goalStatusConfig.on_track.badgeClass
                      }`}
                    >
                      {goalStatusConfig[goalPreview.status]?.label || goalStatusConfig.on_track.label}
                    </span>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Nog nodig</p>
                      <p className="font-mono text-base font-semibold">€{goalPreview.remainingAmount.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Maanden over</p>
                      <p className="font-mono text-base font-semibold">{goalPreview.monthsRemaining || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Benodigd p/m</p>
                      <p className="font-mono text-base font-semibold">€{goalPreview.requiredMonthlyAmount.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Jouw inleg p/m</p>
                      <p className="font-mono text-base font-semibold">€{goalPreview.monthlyContribution.toFixed(2)}</p>
                    </div>
                  </div>
                  {goalPreview.status === 'behind' && (
                    <p className={`mt-3 text-xs ${goalStatusConfig.behind.noteClass}`}>
                      Je ligt ongeveer €{goalPreview.behindAmount.toFixed(2)} per maand achter op schema.
                    </p>
                  )}
                </div>
                <div className="md:col-span-2 flex flex-wrap gap-2">
                  <Button type="submit">
                    {editingGoalId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    {editingGoalId ? 'Opslaan' : 'Toevoegen'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetGoalForm}>
                    <X className="w-4 h-4 mr-2" />
                    Annuleren
                  </Button>
                </div>
              </form>
            )}

            {savingsGoals.length === 0 ? (
              <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
                Nog geen spaardoelen. Voeg er een toe zodra je gericht wilt sparen voor iets concreets.
              </div>
            ) : (
              <div className="space-y-3">
                {savingsGoals.map((goal) => {
                  const progress = Number(goal.progress || 0);
                  const statusConfig = goalStatusConfig[goal.status] || goalStatusConfig.on_track;
                  return (
                    <div key={goal.id} className="space-y-2 rounded-xl border bg-background p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium">{goal.name}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <p className="text-xs text-muted-foreground">
                              {goal.target_date
                                ? `Doel: ${new Date(goal.target_date).toLocaleDateString('nl-NL')}`
                                : `${Number(goal.months_remaining || 0)} maanden resterend`}
                            </p>
                            {goal.goal_pot_name && (
                              <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] text-muted-foreground">
                                Potje: {goal.goal_pot_name}
                              </span>
                            )}
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium ${statusConfig.badgeClass}`}>
                              {statusConfig.label}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="secondary" onClick={() => startEditGoal(goal)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => deleteGoal(goal.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Voortgang</span>
                        <span className="font-mono font-semibold">
                          €{Number(goal.current_amount || 0).toFixed(2)} / €{Number(goal.target_amount || 0).toFixed(2)}
                        </span>
                      </div>
                      <Progress value={progress} className="h-2" />
                      <div className="grid gap-2 text-xs text-muted-foreground md:grid-cols-3">
                        <p>Nu: €{Number(goal.monthly_contribution || 0).toFixed(2)}/maand</p>
                        <p>Benodigd: €{Number(goal.required_monthly_amount || 0).toFixed(2)}/maand</p>
                        <p>Nog nodig: €{Number(goal.remaining_amount || 0).toFixed(2)}</p>
                      </div>
                      {goal.status === 'behind' && (
                        <p className={`text-xs ${statusConfig.noteClass}`}>
                          Verhoog je inleg met ongeveer €{Number(goal.behind_amount || 0).toFixed(2)} per maand om op tijd uit te komen.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
